import * as React from "react"
import {useCallback} from "react"
import {Dialog as DialogPrimitive} from "@base-ui/react/dialog"

import {cn} from "@/lib/utils"
import {Button} from "@/components/ui/button"
import {ChevronLeftIcon, XIcon} from "lucide-react"

function Dialog({...props}: DialogPrimitive.Root.Props) {
    return <DialogPrimitive.Root data-slot="dialog" {...props} />
}

function DialogTrigger({...props}: DialogPrimitive.Trigger.Props) {
    return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({...props}: DialogPrimitive.Portal.Props) {
    return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose({...props}: DialogPrimitive.Close.Props) {
    return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

function DialogOverlay({
                           className,
                           ...props
                       }: DialogPrimitive.Backdrop.Props) {
    return (
        <DialogPrimitive.Backdrop data-slot="dialog-overlay" className={cn(
            "fixed inset-0 isolate z-40 bg-black/10 duration-100 supports-backdrop-filter:backdrop-blur-xs data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0",
            className
        )}
                                  {...props}
        />
    )
}

function DialogContent({
                           className,
                           children,
                           showCloseButton = true,
                           ...props
                       }: DialogPrimitive.Popup.Props & {
    showCloseButton?: boolean
}) {
    return (
        <DialogPortal>
            <DialogOverlay/>
            <DialogPrimitive.Popup data-slot="dialog-content" className={cn(
                "fixed top-1/2 left-1/2 z-40 grid w-full max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 gap-6 rounded-xl bg-popover p-6 text-sm text-popover-foreground ring-1 ring-foreground/10 duration-100 outline-none sm:max-w-md data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95",
                className
            )}
                                   {...props}
            >
                {children}
                {showCloseButton && (
                    <DialogPrimitive.Close data-slot="dialog-close" render={
                        <Button variant="ghost" className="absolute top-4 right-4" size="icon-sm"/>
                    }>
                        <XIcon/>
                        <span className="sr-only">Close</span>
                    </DialogPrimitive.Close>
                )}
            </DialogPrimitive.Popup>
        </DialogPortal>
    )
}

function DialogHeader({className, ...props}: React.ComponentProps<"div">) {
    return (
        <div data-slot="dialog-header" className={cn("flex flex-col gap-2", className)}
             {...props}
        />
    )
}

function DialogFooter({
                          className,
                          showCloseButton = false,
                          children,
                          ...props
                      }: React.ComponentProps<"div"> & {
    showCloseButton?: false | string
}) {
    return (
        <div data-slot="dialog-footer" className={cn(
            "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
            className
        )}
             {...props}
        >
            {children}
            {showCloseButton && (
                <DialogPrimitive.Close render={<Button variant="ghost"/>}>
                    {showCloseButton}
                </DialogPrimitive.Close>
            )}
        </div>
    )
}

function DialogTitle({className, ...props}: DialogPrimitive.Title.Props) {
    return (
        <DialogPrimitive.Title data-slot="dialog-title" className={cn("leading-none font-medium", className)}
                               {...props}
        />
    )
}

function DialogDescription({
                               className,
                               ...props
                           }: DialogPrimitive.Description.Props) {
    return (
        <DialogPrimitive.Description data-slot="dialog-description" className={cn(
            "text-sm text-muted-foreground *:[a]:underline *:[a]:underline-offset-3 *:[a]:hover:text-foreground",
            className
        )}
                                     {...props}
        />
    )
}

function FullscreenDialog({
                              open,
                              onOpenChange,
                              title,
                              render,
                              children
                          }: React.PropsWithChildren<{
    open: boolean,
    onOpenChange: (val: boolean) => void,
    title: string,
    render: DialogPrimitive.Trigger.Props["render"]
}>) {
    const setClose = useCallback(() => {
        onOpenChange(false)
    }, [onOpenChange]);
    const onOpenChangeCont = useCallback((val: boolean) => {
        onOpenChange(val)
    }, [onOpenChange]);
    return (<Dialog open={open} onOpenChange={onOpenChangeCont}>
        <DialogTrigger render={render}/>
        <DialogContent showCloseButton={false}
                       className="h-dvh w-screen max-h-dvh max-w-screen rounded-none p-0 m-0 bg-canvas flex flex-col items-stretch justify-start gap-0 top-0 left-0 translate-x-0 translate-y-0">
            <div className="p-safe-top bg-background shadow-sm">
                <div className="w-full bg-background h-14 py-2 px-3 flex flex-row items-center justify-center">
                    <Button variant="ghost" size="icon-sm" onClick={setClose} className="absolute left-2">
                        <ChevronLeftIcon className="size-6"/>
                    </Button>
                    <span className="text-base font-medium">{title}</span>
                </div>
            </div>
            {children}
        </DialogContent>
    </Dialog>)
}

export {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogOverlay,
    DialogPortal,
    DialogTitle,
    DialogTrigger,
    FullscreenDialog
}
